import { getLanguage, Local, type LanguageCode } from '@/entities/auth/local.enum';
import type { Booking } from '@/entities/bookings/booking.entity';
import { i18n } from '@/i18n';
import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

const locale = i18n.global.locale as unknown as { value: LanguageCode };
const language = ref<LanguageCode>(locale.value);
const tempLanguage = ref<LanguageCode | null>(null);

watch([language, tempLanguage], () => {
  locale.value = tempLanguage.value ?? language.value;
});

export function selectLanguage(next: LanguageCode) {
  if (tempLanguage.value !== null) {
    tempLanguage.value = next;
  } else {
    language.value = next;
  }
}

/** Sets the chosen UI language. A temporary booking locale stays in front until it is cleared. */
export function setBaseLanguage(next: LanguageCode) {
  language.value = next;
  if (tempLanguage.value === null) {
    locale.value = next;
  }
}

export function getBookingProcessLanguage(
  booking: Pick<Booking, 'displayLocale' | 'customerData'>,
): LanguageCode {
  return getLanguage(booking.displayLocale ?? booking.customerData?.preferredLocal ?? Local.EN_US);
}

export function useTemporaryLocale(source: MaybeRefOrGetter<LanguageCode | undefined>) {
  watch(
    () => toValue(source),
    (next) => {
      tempLanguage.value = next ?? null;
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    tempLanguage.value = null;
  });
}
